import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { api } from '../services/api';

// Admin v2 · V4 — Students: inventory + outreach.
//
// Segment chips AND across groups (one selection per group); counts come from
// the server's `segments` (always the full real population, so the chips stay
// stable while the table narrows). Chip state lives in the URL so queue cards
// can deep-link (e.g. /users?activity=never_answered). Tier is the resolver's
// (house rule #5); "opened" (app open) and "answered" (did work) are
// deliberately different numbers and labeled differently.

const CHIP_GROUPS = [
  {
    param: 'tierChip', label: 'TIER',
    chips: (seg) => ['trial', 'free', 'paid', 'override']
      .map((k) => ({ value: k, label: k === 'paid' ? 'Paid' : k[0].toUpperCase() + k.slice(1), count: seg.tier?.[k] || 0 })),
  },
  {
    param: 'activity', label: 'ACTIVITY',
    chips: (seg) => [
      { value: 'answered', label: 'Answered ≥1', count: seg.activity?.answered || 0 },
      { value: 'never_answered', label: 'Never answered', count: seg.activity?.never_answered || 0 },
      { value: 'opened_7d', label: 'Opened app ≤7d', count: seg.activity?.opened_7d || 0 },
      { value: 'no_open_30d', label: 'No open >30d', count: seg.activity?.no_open_30d || 0 },
    ],
  },
  {
    param: 'examCohort', label: 'EXAM DATE',
    chips: (seg) => Object.entries(seg.examDate || {})
      .sort(([a], [b]) => (a === 'unset' ? 1 : b === 'unset' ? -1 : a.localeCompare(b)))
      .map(([k, count]) => ({
        value: k,
        label: k === 'unset' ? 'Unset' : format(new Date(`${k}-01T00:00:00`), 'MMM yyyy'),
        count,
        hint: k === 'unset' ? null : { '2027': '≈ Cl. 12', '2028': '≈ Cl. 11' }[k.slice(0, 4)] || null,
      })),
  },
  {
    param: 'coaching', label: 'PROFILE',
    chips: (seg) => [
      { value: 'coaching', label: 'Coaching', count: seg.coaching?.coaching || 0 },
      { value: 'self', label: 'Self-study', count: seg.coaching?.self || 0 },
    ],
  },
  {
    param: 'targetExam', label: '',
    chips: (seg) => Object.entries(seg.targetExam || {})
      .sort(([, a], [, b]) => b - a)
      .map(([k, count]) => ({ value: k, label: k === 'unset' ? 'Exam unset' : k.replace('JEE ', ''), count })),
  },
  {
    param: 'state', label: 'STATE',
    chips: (seg) => Object.entries(seg.states || {})
      .filter(([k]) => k !== 'unset')
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8)
      .map(([k, count]) => ({ value: k, label: k, count })),
  },
];

function TierBadge({ tierSource, tier, trialDaysLeft }) {
  if (tierSource === 'trial') {
    return <span className="px-2 py-1 rounded-full text-xs font-semibold bg-teacher-purple-light text-teacher-purple whitespace-nowrap">TRIAL{typeof trialDaysLeft === 'number' ? ` · ${trialDaysLeft}d` : ''}</span>;
  }
  if (tierSource === 'subscription') {
    return <span className="px-2 py-1 rounded-full text-xs font-semibold bg-teacher-green-light text-teacher-green whitespace-nowrap">PAID · {tier?.toUpperCase()}</span>;
  }
  if (tierSource === 'override') {
    return <span className="px-2 py-1 rounded-full text-xs font-semibold bg-teacher-blue-light text-teacher-blue whitespace-nowrap">OVERRIDE · {tier?.toUpperCase()}</span>;
  }
  return <span className="px-2 py-1 rounded-full text-xs font-medium bg-teacher-bg-secondary text-teacher-text-primary">FREE</span>;
}

const waLink = (phone) => `https://wa.me/${String(phone || '').replace(/\D/g, '')}`;

export default function StudentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [offset, setOffset] = useState(0);
  // Server-side sort (the list is paginated, so browser sorting would only
  // order the visible page). Default: most recently active first.
  const [sort, setSort] = useState({ by: 'lastActive', dir: 'desc' });
  const PAGE_SIZE = 50;

  // Text columns read A→Z first; numbers and dates start highest/newest-first.
  const DEFAULT_DIR = {
    name: 'asc', phone: 'asc', tier: 'desc', examDate: 'asc',
    questions: 'desc', lastActive: 'desc', signup: 'desc', aiCost: 'desc', build: 'desc', device: 'asc',
  };
  const handleSort = (key) => {
    setOffset(0);
    setSort((s) => s.by === key
      ? { by: key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { by: key, dir: DEFAULT_DIR[key] || 'asc' });
  };
  const SortHeader = ({ sortKey, children, align = 'left' }) => {
    const active = sort.by === sortKey;
    return (
      <th className={`text-${align} px-4 py-2.5`}>
        <button
          type="button"
          onClick={() => handleSort(sortKey)}
          aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
          className={`inline-flex items-center gap-1 text-[11px] font-semibold tracking-wide transition-colors hover:text-primary-700 ${
            active ? 'text-primary-700' : 'text-teacher-text-tertiary'
          }`}
        >
          {children}
          <span className={active ? 'opacity-100' : 'opacity-30'}>
            {active ? (sort.dir === 'asc' ? '▲' : '▼') : '▴▾'}
          </span>
        </button>
      </th>
    );
  };

  const chipParams = useMemo(() => {
    const p = {};
    for (const g of CHIP_GROUPS) {
      const v = searchParams.get(g.param);
      if (v) p[g.param] = v;
    }
    return p;
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api.getUsers({
        ...chipParams,
        search,
        limit: PAGE_SIZE,
        offset,
        sortBy: sort.by,
        sortDir: sort.dir,
      })
        .then((r) => { if (!cancelled) { setData(r.data); setError(null); } })
        .catch((e) => { if (!cancelled) setError(e.message); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, search ? 300 : 0); // debounce typing only
    return () => { cancelled = true; clearTimeout(t); };
  }, [chipParams, search, offset, sort]);

  const toggleChip = (param, value) => {
    const next = new URLSearchParams(searchParams);
    if (next.get(param) === value) next.delete(param);
    else next.set(param, value);
    setSearchParams(next, { replace: true });
    setOffset(0);
  };

  const activeFilterCount = Object.keys(chipParams).length;
  const seg = data?.segments || {};

  const exportCsv = () => {
    if (!data?.users?.length) return;
    const cols = ['name', 'phone', 'tier', 'tierSource', 'trialDaysLeft', 'examDate', 'targetExam', 'state', 'coaching', 'answers', 'lastActive', 'signedUp'];
    const rows = data.users.map((u) => [
      `${u.firstName} ${u.lastName}`.trim(), u.phone, u.tier, u.tierSource,
      u.trialDaysLeft ?? '', u.examDate ?? '', u.targetExam ?? '', u.state ?? '',
      u.isEnrolledInCoaching ? 'yes' : 'no', u.totalQuestions,
      u.lastActive ?? '', u.createdAt ?? '',
    ]);
    const csv = [cols, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `students-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-baseline gap-3.5">
          <h1 className="text-2xl font-bold text-teacher-text-primary">Students</h1>
          <span className="text-sm text-teacher-text-secondary">
            {seg.total ?? '…'} real · inventory &amp; outreach
            {data?.testUserCount ? ` · ${data.testUserCount} internal excluded` : ''}
          </span>
        </div>
        <div className="flex items-center gap-2.5">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            placeholder="name or phone…"
            className="bg-white border border-teacher-border rounded-lg px-3 py-2 text-sm w-56 focus:outline-none focus:border-primary-400"
          />
          <button
            onClick={exportCsv}
            className="border border-teacher-border-strong rounded-lg px-3.5 py-2 text-sm font-medium text-teacher-text-secondary bg-white hover:bg-teacher-bg transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Segment chips */}
      <div className="bg-white border border-teacher-border rounded-xl px-4 py-3.5 mb-4 flex flex-col gap-2.5">
        {CHIP_GROUPS.map((g) => (
          <div key={g.param} className="flex items-center gap-2 flex-wrap">
            <div className="text-[11px] font-semibold tracking-wide text-teacher-text-tertiary w-16 shrink-0">{g.label}</div>
            {g.chips(seg).map((c) => {
              const active = searchParams.get(g.param) === c.value;
              return (
                <button
                  key={c.value}
                  onClick={() => toggleChip(g.param, c.value)}
                  className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                    active
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : c.count === 0
                        ? 'border-teacher-border text-teacher-text-tertiary'
                        : 'border-teacher-border text-teacher-text-secondary hover:border-primary-300 hover:text-primary-700'
                  }`}
                >
                  {c.label} <span className={active ? 'font-bold' : 'font-bold text-teacher-text-primary'}>{active ? `· ${c.count} ✕` : c.count}</span>
                  {c.hint && <span className="text-teacher-text-tertiary font-normal"> · {c.hint}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {error && <div className="text-sm text-teacher-red mb-3">Failed to load: {error}</div>}

      {/* Table */}
      <div className="bg-white border border-teacher-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-teacher-bg border-b border-teacher-border">
                <SortHeader sortKey="name">STUDENT</SortHeader>
                <SortHeader sortKey="phone">PHONE</SortHeader>
                <SortHeader sortKey="tier">TIER</SortHeader>
                <SortHeader sortKey="examDate">EXAM</SortHeader>
                <th className="text-center px-4 py-2.5 text-[11px] font-semibold tracking-wide text-teacher-text-tertiary">COACHING</th>
                <SortHeader sortKey="questions" align="right">ANSWERS</SortHeader>
                <SortHeader sortKey="aiCost" align="right">AI COST</SortHeader>
                <SortHeader sortKey="build" align="right">BUILD</SortHeader>
                <SortHeader sortKey="device">DEVICE</SortHeader>
                <SortHeader sortKey="lastActive">LAST ACTIVE</SortHeader>
                <SortHeader sortKey="signup">SIGNED UP</SortHeader>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                <tr><td colSpan="12" className="px-4 py-8 text-center text-teacher-text-tertiary">Loading…</td></tr>
              )}
              {data?.users?.map((u) => (
                <tr key={u.uid} className="border-b border-teacher-bg-secondary hover:bg-teacher-bg/60">
                  <td className="px-4 py-2.5">
                    <span className="font-semibold text-teacher-text-primary">{`${u.firstName} ${u.lastName}`.trim() || '(no name)'}</span>
                    {u.state && <span className="text-xs text-teacher-text-tertiary"> · {u.state}</span>}
                    {u.isTestUser && <span className="ml-1.5 text-[10px] font-semibold text-teacher-amber">OURS</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-2 text-teacher-text-secondary">
                      {u.phone || '—'}
                      {u.phone && (
                        <a href={waLink(u.phone)} target="_blank" rel="noopener noreferrer" title="WhatsApp" className="text-teacher-green hover:opacity-70">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
                          </svg>
                        </a>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2.5"><TierBadge tierSource={u.tierSource} tier={u.tier} trialDaysLeft={u.trialDaysLeft} /></td>
                  <td className="px-4 py-2.5 text-teacher-text-secondary whitespace-nowrap">
                    {u.examDate ? format(new Date(`${u.examDate}-01T00:00:00`), 'MMM yyyy') : '—'}
                    {u.targetExam && <span className="text-xs text-teacher-text-tertiary"> · {u.targetExam.includes('Advanced') ? 'M+A' : 'Main'}</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center text-teacher-text-secondary">{u.isEnrolledInCoaching ? '✓' : '—'}</td>
                  <td className={`px-4 py-2.5 text-right font-semibold ${u.totalQuestions ? 'text-teacher-text-primary' : 'text-teacher-red'}`}>{u.totalQuestions}</td>
                  {/* Who is costing us money: Snap & Solve (Sonnet vision) + Priya tokens, same estimate as the Revenue hub card */}
                  <td className="px-4 py-2.5 text-right whitespace-nowrap" title={`${u.ai?.snaps || 0} snaps · ${u.ai?.tutorMessages || 0} Priya msgs (${((u.ai?.tutorTokens || 0) / 1000).toFixed(0)}K tokens) · estimate`}>
                    {u.ai?.costUsd ? (
                      <>
                        <span className="font-semibold text-teacher-text-primary">${u.ai.costUsd.toFixed(2)}</span>
                        <span className="block text-[11px] text-teacher-text-tertiary">
                          {u.ai.snaps ? `${u.ai.snaps} snap${u.ai.snaps === 1 ? '' : 's'}` : ''}
                          {u.ai.snaps && u.ai.tutorMessages ? ' · ' : ''}
                          {u.ai.tutorMessages ? `${u.ai.tutorMessages} Priya` : ''}
                        </span>
                      </>
                    ) : <span className="text-teacher-text-tertiary">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap" title={u.appVersion ? `App ${u.appVersion}` : 'Not reported — on a build before 162, or not opened since'}>
                    {u.appBuild ? <span className="text-teacher-text-secondary">{u.appBuild}</span> : <span className="text-teacher-text-tertiary">&lt;162</span>}
                  </td>
                  {/* Current session's device: Android model as the phone reports it, or Web. Overwritten on every login. */}
                  <td className="px-4 py-2.5 text-teacher-text-secondary whitespace-nowrap" title={u.device ? 'Device of the current session' : 'No session yet'}>
                    {u.device ? (
                      u.device === 'Web'
                        ? <span className="inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold bg-teacher-bg text-teacher-text-secondary">Web</span>
                        : <span className="text-xs">{u.device}</span>
                    ) : <span className="text-teacher-text-tertiary">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-teacher-text-secondary whitespace-nowrap">
                    {u.lastActive ? formatDistanceToNow(new Date(u.lastActive), { addSuffix: true }).replace('about ', '') : 'Never'}
                  </td>
                  <td className="px-4 py-2.5 text-teacher-text-secondary whitespace-nowrap">
                    {u.createdAt ? format(new Date(u.createdAt), 'MMM d') : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Link to={`/users/${u.uid}`} className="text-xs font-semibold text-primary-700 hover:text-primary-800">open</Link>
                  </td>
                </tr>
              ))}
              {data && data.users.length === 0 && (
                <tr><td colSpan="12" className="px-4 py-8 text-center text-teacher-text-tertiary">No students match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 bg-teacher-bg border-t border-teacher-border text-xs text-teacher-text-secondary">
          <span>
            {data ? `${data.total} student${data.total === 1 ? '' : 's'} match` : '…'}
            {activeFilterCount > 0 && ` · ${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'}`}
            {` · sorted by ${{ name: 'name', phone: 'phone', tier: 'tier', examDate: 'exam date', questions: 'answers', lastActive: 'last active', signup: 'signup date' }[sort.by]} ${sort.dir === 'asc' ? '↑' : '↓'} · times IST`}
          </span>
          <span className="flex items-center gap-3">
            {offset > 0 && <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="font-semibold text-primary-700 hover:text-primary-800">← prev</button>}
            <span>{data ? `${Math.min(offset + 1, data.total)}–${Math.min(offset + PAGE_SIZE, data.total)} of ${data.total}` : ''}</span>
            {data?.hasMore && <button onClick={() => setOffset(offset + PAGE_SIZE)} className="font-semibold text-primary-700 hover:text-primary-800">next →</button>}
          </span>
        </div>
      </div>

      <p className="text-xs text-teacher-text-tertiary mt-2.5">
        Chips AND across groups · counts are the full real population and don't change as you filter ·
        tier = resolveEffectiveTier (override → paid → trial → free), never the raw doc
      </p>
    </div>
  );
}
